import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Ticket } from 'lucide-react';
import CompanyLogo from '../../components/CompanyLogo';
import { formatDistanceToNow } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useAuth } from '../../contexts/AuthContext';

export const meta = {
  id: 'recent_tickets',
  category: 'tickets',
  titleKey: 'dashboard.widgets.recentTickets',
  descriptionKey: 'dashboard.widgets.recentTicketsDesc',
  defaultSize: 'md',
  icon: Ticket,
};

// This widget receives recentTickets via ctx because the parent page already
// fetches them as part of /dashboard/stats (no second request needed).
export default function RecentTicketsWidget({ ctx }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const locale = user?.language === 'en' ? enUS : es;
  const recentTickets = ctx?.recentTickets || [];

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', height: '100%' }}>
      <div className="card-header" style={{ padding: '14px 20px', borderBottom: '1px solid var(--c-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="card-title">{t('dashboard.recentActivity')}</div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/tickets')}>
          {t('dashboard.viewAll', 'View all')} →
        </button>
      </div>
      {recentTickets.length === 0 ? (
        <div className="empty-state"><Ticket size={40} /><h3>{t('dashboard.noOpenTickets', 'No open tickets')}</h3></div>
      ) : (
        recentTickets.map((ticket) => (
          <div
            key={ticket.id || ticket.ticket_number}
            className={`ticket-row ticket-row-${ticket.priority}`}
            onClick={() => navigate(`/tickets/${ticket.id}`)}
          >
            <span className="ticket-number">#{ticket.ticket_number}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="ticket-subject">{ticket.subject}</div>
              <div className="ticket-contact" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {ticket.contact_avatar && (
                  <img src={ticket.contact_avatar} alt="" style={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                )}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {ticket.contact_name || ticket.contact_email}
                </span>
                {ticket.company_name && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 4, opacity: 0.7 }}>
                    ·
                    {ticket.company_logo_url && (
                      <CompanyLogo name={ticket.company_name} logoUrl={ticket.company_logo_url} size={12} tile={false} />
                    )}
                    {ticket.company_name}
                  </span>
                )}
              </div>
            </div>
            <span className={`badge badge-${ticket.priority}`}>{t(`tickets.priorities.${ticket.priority}`)}</span>
            <span className={`badge badge-${ticket.status}`}>{t(`tickets.statuses.${ticket.status}`)}</span>
            <span style={{ fontSize: 11, color: 'var(--c-text-subtle)', whiteSpace: 'nowrap' }}>
              {formatDistanceToNow(new Date(ticket.updated_at), { addSuffix: true, locale })}
            </span>
          </div>
        ))
      )}
    </div>
  );
}
